// DEPRECATED: Use the channel specific components. This is kept for backwards compatibility
import React, { cloneElement, isValidElement, useContext } from 'react';
import PropTypes from 'prop-types';
import { useDataModel } from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Row, Col } from '@thd-olt-component-react/grid';
import { Heading } from '@thd-olt-component-react/heading';
import { Card } from '@thd-olt-component-react/card';
import { dataModel } from '../dataModel';
import { getSortedValue } from '../helpers';
import './specifications.scss';

const GROUP_TYPES = {
  DIMENSIONS: 'Dimensions',
  DETAILS: 'Details',
  WARRANTY: 'Warranty / Certifications'
};

const Specifications = (props) => {

  const {
    itemId,
    hideMobileFade,
    children
  } = props;
  const { channel } = useContext(ExperienceContext);

  const getAttrContent = (attributeGroupList) => {
    const attributeValues = getSortedValue({ attributeGroupList });
    return (
      <>
        <div className="specifications__wrapper">
          {(attributeValues || []).map((attr, i) => (
            <Col key={i} xs="12" sm="12" md="6" className="specifications__row">
              <Col fallback="6" className="specs__cell specs__cell__label">
                {attr.specName}
              </Col>
              <Col fallback="6" className="specs__cell">
                {attr.specValue}
              </Col>
            </Col>
          ))}
          {attributeValues && attributeValues.length % 2 === 1 && (
            <Col xs="12" sm="12" md="6" className="specifications__row">
              <Col fallback="6" className="specs__cell__empty specs__cell__label" />
              <Col fallback="6" className="specs__cell__empty" />
            </Col>
          )}
        </div>
      </>
    );
  };

  const getRenderingContent = (specificationGroup) => (
    <>
      {(specificationGroup)
        .filter((group) => group.specTitle === GROUP_TYPES.DIMENSIONS)
        .map((attrGroupList, i) => (
          <Row key={i}>
            <Col nopadding>
              <h4 className="specifications__title">{attrGroupList.specTitle}</h4>
              {getAttrContent(attrGroupList)}
            </Col>
          </Row>
        ))}
      {(specificationGroup)
        .filter((group) => group.specTitle === GROUP_TYPES.DETAILS)
        .map((attrGroupList, i) => (
          <Row key={i}>
            <Col nopadding>
              <h4 className="specifications__title specifications__title__paddingtop">{attrGroupList.specTitle}</h4>
              {getAttrContent(attrGroupList)}
            </Col>
          </Row>
        ))}
      {(specificationGroup)
        .filter((group) => group.specTitle === GROUP_TYPES.WARRANTY)
        .map((attrGroupList, i) => (
          <Row key={i}>
            <Col nopadding>
              <h4 className="specifications__title specifications__title__paddingtop">{attrGroupList.specTitle}</h4>
              {getAttrContent(attrGroupList)}
            </Col>
          </Row>
        ))}
      <Row>
        <Col nopadding>
          <div id="thdFeedbackPip" className="specifications__link" suppressHydrationWarning />
        </Col>
      </Row>
    </>
  );

  const renderDesktopSpecification = (renderedContent) => (
    <Row>
      <Heading title="Specifications" underline />
      {children && cloneElement(children, {
        itemId,
        maxfeatures: 8,
        minfeatures: 3,
        channel,
        displaySimilarLinks: true
      })}
      {renderedContent}
    </Row>
  );

  const renderMobileSpecification = (renderedContent) => (
    <Card noHide>
      <Card.Content
        title={hideMobileFade ? null : 'Specifications'}
        className="specifications--mobile"
        maxHeight={isValidElement(children) || hideMobileFade ? 'none' : '219px'}
        fade={!isValidElement(children) && !hideMobileFade}
      >
        {children
          ? cloneElement(children, {
            itemId,
            maxfeatures: 4,
            minfeatures: 4,
            channel
          })
          : renderedContent}
        <Card.Content.Overlay>
          {children && cloneElement(children, {
            itemId,
            maxfeatures: 8,
            minfeatures: 4,
            channel,
            displaySimilarLinks: true
          })}
          {renderedContent}
        </Card.Content.Overlay>
      </Card.Content>
    </Card>
  );

  const renderSpecs = (specificationGroup) => {
    const renderedContent = getRenderingContent(specificationGroup);
    return (
      <Row
        className="specifications"
        id="specifications"
        style={{ pointerEvents: 'auto' }}
      >
        {channel !== 'mobile'
          ? renderDesktopSpecification(renderedContent)
          : renderMobileSpecification(renderedContent)}
      </Row>
    );
  };

  const { data, loading, error } = useDataModel('product', { variables: { itemId } });

  if (loading && !data) {
    return null;
  }

  if (error && !data) {
    return null;
  }

  if (!data?.product?.specificationGroup) {
    return null;
  }

  const { specificationGroup } = data.product;

  if (!specificationGroup.length) {
    return null;
  }

  return renderSpecs(specificationGroup);
};

Specifications.displayName = 'Specifications';

Specifications.propTypes = {
  /** SKU Number */
  itemId: PropTypes.string.isRequired,
  /** Can take an (optional) single child component of type KeyProductFeatures */
  children: (props, propName, componentName) => {
    const prop = props[propName];
    let error;
    if (prop && (!isValidElement(prop) || prop.type.name !== 'KeyProductFeatures')) {
      error = new Error(`'${componentName}' only accepts a single child of type 'KeyProductFeatures'`);
    }
    return error;
  }
};

Specifications.defaultProps = {
  hideMobileFade: false
};

Specifications.dataModel = dataModel;

export { Specifications };
